// WORKOUT page — ported from the original app's WORKOUT section.
import { S } from '../state.js';
import { save } from '../lib/sync.js';
import { today, fmtDate, fmtDateShort, shiftDate, weekdayShort } from '../lib/dates.js';
import { IC, cicon } from '../lib/icons.js';
import { openSheet, closeSheet, openModal, closeModal } from '../lib/modal.js';
import { showConfirm } from '../components/confirmDialog.js';
import { registerPage } from '../lib/router.js';
import { openDatePicker } from '../components/datePicker.js';

let wkDate = today();
let wkEditId = null;
let wkCardioOn = false;
let wkStrength = null;
let wkRestOn = false;

export function shiftWkDate(n) {
  wkDate = shiftDate(wkDate, n);
  renderWorkout();
}
export function setWkDate(d) { wkDate = d; renderWorkout(); }
export function getWkDate() { return wkDate; }

export function openWkDatePicker() {
  openDatePicker(wkDate, d => { wkDate = d; renderWorkout(); }, d => S.workoutLog.some(w => w.date === d));
}

// Strength-group name → icon slug, for the big current-day card and the upcoming preview.
const STRENGTH_ICON = { 'Верх': 'upper', 'Ноги': 'legs', 'Пресс': 'abs' };

export function renderWorkout() {
  document.getElementById('wkDateLabel').textContent = fmtDate(wkDate);
  renderWkCurrent();
  renderWkUpcoming();
}
registerPage('workout', renderWorkout);

function renderWkCurrent() {
  const el = document.getElementById('wkCurrent');
  const w = S.workoutLog.find(x => x.date === wkDate);
  const idx = w ? S.workoutLog.indexOf(w) : -1;

  if (!w) {
    el.innerHTML = `<div class="empty"><div class="empty-icon">${cicon('dumbbell', 28)}</div><div class="empty-text">Нет записей</div><div class="empty-sub">Нажми + чтобы добавить</div></div>`;
    return;
  }

  const acts = `<div class="wk-big-acts">
    <button class="btn btn-secondary btn-sm" style="flex:1" onclick="editWorkout(${idx})">Изменить</button>
    <button class="btn btn-danger btn-sm btn-icon" onclick="delWorkout(${idx})">${IC.trash}</button>
  </div>`;

  // Apple Watch / health-import can still write steps/calories/duration on a rest day
  // (rest is a manual "no workout" classification, not "no phone data") — show them
  // whenever present, rest day or not, instead of hiding the whole stats block.
  const statsHtml = `<div class="stat-row stat-row-3 wk-big-stats">
    <div class="stat-block"><div class="wk-stat-icon">${cicon('shoe', 20)}</div><div class="stat-num t-num">${w.steps || '—'}</div><div class="stat-label">шаги</div></div>
    <div class="stat-block"><div class="wk-stat-icon">${cicon('fire', 20)}</div><div class="stat-num t-num">${w.kcalBurned || '—'}</div><div class="stat-label">ккал</div></div>
    <div class="stat-block"><div class="wk-stat-icon">${cicon('chart', 20)}</div><div class="stat-num t-num">${w.duration || '—'}</div><div class="stat-label">минут</div></div>
  </div>`;

  if (w.rest) {
    el.innerHTML = `<div class="wk-big-card">
      <div class="wk-rest-big">
        ${cicon('moon', 44)}
        <div class="wk-rest-big-label">Выходной день</div>
      </div>
      ${statsHtml}
      ${w.note ? `<div class="wk-big-note">${cicon('chat', 14)} ${w.note}</div>` : ''}
      ${acts}
    </div>`;
    return;
  }

  const types = [];
  if (w.cardio) types.push({ icon: 'cardio', label: 'Кардио' });
  if (w.strength) types.push({ icon: STRENGTH_ICON[w.strength] || 'dumbbell', label: w.strength });
  const typesHtml = types.length
    ? types.map(t => `<div class="wk-big-type">${cicon(t.icon, 40)}<div class="wk-big-label">${t.label}</div></div>`).join('')
    : `<div class="wk-big-type">${cicon('dumbbell', 40)}<div class="wk-big-label">Тренировка</div></div>`;

  el.innerHTML = `<div class="wk-big-card">
    <div class="wk-big-types">${typesHtml}</div>
    ${statsHtml}
    ${w.note ? `<div class="wk-big-note">${cicon('chat', 14)} ${w.note}</div>` : ''}
    ${acts}
  </div>`;
}

// Compact preview of the next 2 days after the one currently shown — "what's the plan".
// Only reads existing entries (nothing auto-generated); labels the first day "Завтра"
// only when actually looking at real today, otherwise both rows show plain dates so
// browsing past/future days never shows a misleading "Завтра".
function renderWkUpcoming() {
  const el = document.getElementById('wkUpcoming');
  const td = today();
  const days = [shiftDate(wkDate, 1), shiftDate(wkDate, 2)];
  el.innerHTML = days.map((d, i) => {
    const label = (wkDate === td && i === 0) ? 'Завтра' : fmtDateShort(d);
    const w = S.workoutLog.find(x => x.date === d);
    // No real entry yet? Fall back to the plan for that specific date, labeled "план" so
    // it's clearly not confirmed data — see openWeeklyPlan().
    const src = w || S.weeklyPlan?.[d];
    const isPlan = !w && !!src;
    let typesHtml = `<span class="wk-upcoming-empty">нет данных</span>`;
    if (src) {
      if (src.rest) {
        typesHtml = `${cicon('moon', 15)} Выходной`;
      } else {
        const parts = [];
        if (src.cardio) parts.push(cicon('cardio', 15) + ' Кардио');
        if (src.strength) parts.push(cicon(STRENGTH_ICON[src.strength] || 'dumbbell', 15) + ' ' + src.strength);
        typesHtml = parts.length ? parts.join(' &nbsp; ') : `${cicon('dumbbell', 15)} Тренировка`;
      }
      if (isPlan) typesHtml += ` <span class="tag tag-neutral" style="margin-left:4px">план</span>`;
    }
    return `<div class="wk-upcoming-row">
      <div class="wk-upcoming-date">${label}</div>
      <div class="wk-upcoming-types">${typesHtml}</div>
    </div>`;
  }).join('');
}

function updateWkRestUI() {
  document.getElementById('wkRestBtn').classList.toggle('on', wkRestOn);
  document.getElementById('wkCardioRow').style.opacity = wkRestOn ? '.4' : '';
  document.getElementById('wkStrRow').style.opacity = wkRestOn ? '.4' : '';
  document.getElementById('wkCardioRow').style.pointerEvents = wkRestOn ? 'none' : '';
  document.getElementById('wkStrRow').style.pointerEvents = wkRestOn ? 'none' : '';
}

export function toggleWkRest() {
  wkRestOn = !wkRestOn;
  if (wkRestOn) {
    wkCardioOn = false; wkStrength = null;
    document.getElementById('wkCardioBtn').classList.remove('on');
    document.querySelectorAll('#wkStrGroup .toggle-btn').forEach(b => b.classList.remove('on'));
  }
  updateWkRestUI();
}

export function openAddWorkout() {
  wkEditId = null;
  // If there's a plan for this exact date and no real entry yet, prefill from it (still
  // just a starting point — every toggle stays fully editable before saving).
  const plan = !S.workoutLog.some(w => w.date === wkDate) ? S.weeklyPlan?.[wkDate] : null;
  wkCardioOn = plan?.cardio || false;
  wkStrength = plan?.strength || null;
  wkRestOn = plan?.rest || false;
  document.getElementById('wkSheetTitle').textContent = 'Тренировка — ' + fmtDateShort(wkDate);
  document.getElementById('wkCardioBtn').classList.toggle('on', wkCardioOn);
  document.querySelectorAll('#wkStrGroup .toggle-btn').forEach(b => {
    b.classList.toggle('on', b.textContent.trim() === wkStrength || (wkStrength === null && b.textContent.trim() === 'Нет'));
  });
  document.getElementById('wkKcal').value = '';
  document.getElementById('wkSteps').value = '';
  document.getElementById('wkDur').value = '';
  document.getElementById('wkNote').value = '';
  setWkDetailsOpen(false);
  updateWkRestUI();
  openSheet('shAddWorkout');
}

export function editWorkout(idx) {
  const w = S.workoutLog[idx];
  wkEditId = idx;
  wkCardioOn = w.cardio;
  wkStrength = w.strength;
  wkRestOn = !!w.rest;
  document.getElementById('wkSheetTitle').textContent = 'Редактировать';
  document.getElementById('wkCardioBtn').classList.toggle('on', w.cardio);
  document.querySelectorAll('#wkStrGroup .toggle-btn').forEach(b => {
    b.classList.toggle('on', b.textContent.trim() === w.strength || (w.strength === null && b.textContent.trim() === 'Нет'));
  });
  document.getElementById('wkKcal').value = w.kcalBurned || '';
  document.getElementById('wkSteps').value = w.steps || '';
  document.getElementById('wkDur').value = w.duration || '';
  document.getElementById('wkNote').value = w.note || '';
  setWkDetailsOpen(!!(w.kcalBurned || w.steps || w.duration || w.note));
  updateWkRestUI();
  openSheet('shAddWorkout');
}

export function toggleWkDetails() {
  const block = document.getElementById('wkDetailsBlock');
  setWkDetailsOpen(block.style.display === 'none');
}
function setWkDetailsOpen(open) {
  document.getElementById('wkDetailsBlock').style.display = open ? '' : 'none';
  document.getElementById('wkDetailsChevron').textContent = open ? '▴' : '▾';
}

export function toggleWkCardio() {
  wkCardioOn = !wkCardioOn;
  wkRestOn = false;
  document.getElementById('wkCardioBtn').classList.toggle('on', wkCardioOn);
  updateWkRestUI();
}

export function setWkStr(btn, val) {
  wkStrength = val;
  wkRestOn = false;
  document.querySelectorAll('#wkStrGroup .toggle-btn').forEach(b => b.classList.remove('on'));
  btn.classList.add('on');
  updateWkRestUI();
}

export function saveWorkout() {
  const entry = {
    date: wkDate,
    rest: wkRestOn,
    cardio: wkCardioOn,
    strength: wkStrength,
    kcalBurned: parseInt(document.getElementById('wkKcal').value) || 0,
    steps: parseInt(document.getElementById('wkSteps').value) || 0,
    duration: parseInt(document.getElementById('wkDur').value) || 0,
    note: document.getElementById('wkNote').value.trim(),
  };
  if (wkEditId !== null) {
    S.workoutLog[wkEditId] = entry;
  } else {
    // A record for this date might already exist (e.g. marked "выходной" earlier, now a
    // real workout is being logged) — replace it rather than pushing a duplicate (see
    // known-bugs #5: always check for an existing entry before pushing a new log row).
    const existingIdx = S.workoutLog.findIndex(w => w.date === wkDate);
    if (existingIdx >= 0) S.workoutLog[existingIdx] = entry;
    else S.workoutLog.push(entry);
  }
  save(true);
  closeSheet('shAddWorkout');
  renderWorkout();
}

// ── Weekly plan (new feature) — a plan for each of the next 7 real calendar dates
// (not a recurring Mon-Sun template, since irregular schedules don't repeat weekly),
// shown in the upcoming-days preview whenever a real entry doesn't exist yet for that
// date. Purely a display aid: it never writes a workoutLog entry on its own, only the
// person logging a real day does.
export function openWeeklyPlan() {
  const plan = S.weeklyPlan || {};
  const dates = Array.from({ length: 7 }, (_, i) => shiftDate(today(), i));
  document.getElementById('weeklyPlanBody').innerHTML = dates.map(d => {
    const p = plan[d] || { rest: false, cardio: false, strength: null };
    return `<div class="card card-sm" style="margin-bottom:8px">
      <div style="font-size:13px;font-weight:700;margin-bottom:8px">${weekdayShort(d)}, ${fmtDateShort(d)}</div>
      <div class="toggle-group" style="margin-bottom:8px">
        <button type="button" class="toggle-btn ${p.rest ? 'on' : ''}" onclick="toggleWeeklyPlanRest('${d}',this)">Выходной</button>
        <button type="button" class="toggle-btn ${!p.rest && p.cardio ? 'on' : ''}" ${p.rest ? 'disabled style="opacity:.4"' : ''} onclick="toggleWeeklyPlanCardio('${d}',this)">Кардио</button>
      </div>
      <div class="toggle-group" id="weeklyPlanStr${d}" style="${p.rest ? 'opacity:.4;pointer-events:none' : ''}">
        ${['Верх', 'Ноги', 'Пресс'].map(s => `<button type="button" class="toggle-btn ${p.strength === s ? 'on' : ''}" onclick="setWeeklyPlanStr('${d}',this,'${s}')">${s}</button>`).join('')}
        <button type="button" class="toggle-btn ${!p.strength ? 'on' : ''}" onclick="setWeeklyPlanStr('${d}',this,null)">Нет</button>
      </div>
    </div>`;
  }).join('');
  openModal('shWeeklyPlan');
}

function getPlanDay(dateStr) {
  if (!S.weeklyPlan) S.weeklyPlan = {};
  if (!S.weeklyPlan[dateStr]) S.weeklyPlan[dateStr] = { rest: false, cardio: false, strength: null };
  return S.weeklyPlan[dateStr];
}

export function toggleWeeklyPlanRest(dateStr, btn) {
  const p = getPlanDay(dateStr);
  p.rest = !p.rest;
  if (p.rest) { p.cardio = false; p.strength = null; }
  save(true);
  openWeeklyPlan();
}
export function toggleWeeklyPlanCardio(dateStr, btn) {
  const p = getPlanDay(dateStr);
  p.cardio = !p.cardio;
  save(true);
  btn.classList.toggle('on', p.cardio);
}
export function setWeeklyPlanStr(dateStr, btn, val) {
  const p = getPlanDay(dateStr);
  p.strength = val;
  save(true);
  document.querySelectorAll(`#weeklyPlanStr${dateStr} .toggle-btn`).forEach(b => b.classList.remove('on'));
  btn.classList.add('on');
}

export function delWorkout(idx) {
  showConfirm('Удалить тренировку?', () => {
    S.workoutLog.splice(idx, 1);
    save(true); renderWorkout();
  });
}
